import { ReactNode, ElementType } from "react";

export type TypeVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "body1"
  | "body2"
  | "caption"
  | "small"
  | "tiny";

export type Props = {
  /** Typography variant */
  variant?: TypeVariant;
  /** HTML element to render as */
  as?: ElementType;
  /** Content to display */
  children: ReactNode;
  /** Element ID */
  id?: string;
  /** Additional CSS classes */
  className?: string;
  /** Tab index for focus management */
  tabIndex?: number;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Allow any additional HTML attributes */
  [key: string]: any;
};

/**
 * Base typography component with no styling.
 * Provides semantic HTML elements based on variant.
 * Use this as the foundation for styled typography components.
 *
 * @example
 * // In shared/ui/Type.tsx
 * import { TypeBase, type Props as TypeBaseProps } from "@core/ui";
 *
 * export function Type({ variant = "h1", className, ...props }: TypeBaseProps) {
 *   const classes: Record<TypeVariant, string> = {
 *     h1: "text-4xl font-bold",
 *     // ... other variants
 *   };
 *
 *   return (
 *     <TypeBase
 *       variant={variant}
 *       className={twMerge(classes[variant], className)}
 *       {...props}
 *     />
 *   );
 * }
 */
export const TypeBase = ({
  variant = "h1",
  as,
  children,
  id,
  className,
  tabIndex,
  "data-testid": testId = "type",
  ...props
}: Props) => {
  // Default semantic element based on variant
  const defaultElement: Record<TypeVariant, ElementType> = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    body1: "p",
    body2: "p",
    caption: "span",
    small: "span",
    tiny: "span",
  };

  const Component = as || defaultElement[variant];

  return (
    <Component
      id={id}
      className={className}
      tabIndex={tabIndex}
      data-testid={testId}
      {...props}
    >
      {children}
    </Component>
  );
};

TypeBase.displayName = "TypeBase";

export default TypeBase;
